import { motion } from 'framer-motion'
import { FaHeart, FaShieldAlt, FaUsers, FaLightbulb } from 'react-icons/fa'

const AboutPage = () => {
  const values = [
    {
      icon: <FaHeart className="w-12 h-12" />,
      title: 'Client-Centered',
      description: 'We build tools that help you provide better care and build stronger client relationships.'
    },
    {
      icon: <FaShieldAlt className="w-12 h-12" />,
      title: 'Security First',
      description: 'Your clients\' privacy and data security are our top priorities. HIPAA-compliant from day one.'
    },
    {
      icon: <FaUsers className="w-12 h-12" />,
      title: 'Community Driven',
      description: 'Built by practitioners, for practitioners. We listen to your feedback and continuously improve.'
    },
    {
      icon: <FaLightbulb className="w-12 h-12" />,
      title: 'Innovation',
      description: 'We stay ahead of industry trends to bring you the most advanced practice management tools.'
    }
  ]

  const timeline = [
    {
      year: '2005',
      title: 'The Foundation',
      description: 'Started with 20 years of digital marketing expertise serving medical and wellness professionals, building innovative solutions that solve real-world problems.'
    },
    {
      year: '2023',
      title: 'Acuity Identified',
      description: 'Recognized a critical gap in the market: wellness practitioners were overwhelmed by disconnected tools and fragmented software. We knew we could build something better.'
    },
    {
      year: '2024',
      title: 'Platform Launch',
      description: 'Launched Acuity Practice with core features: client management, session notes, and scheduling—built on years of industry insights and technical expertise.'
    },
    {
      year: 'Now & Beyond',
      title: 'Growing Impact',
      description: 'Transforming how wellness practitioners work, proving that great technology built by people who understand healthcare can simplify practice management.'
    }
  ]

  const team = [
    {
      name: 'Bevon Findley',
      role: 'Founder & CEO',
      bio: 'Digital marketing expert with 20 years of experience building solutions for medical and wellness professionals. Led the vision for Acuity Practice.',
      image: null
    },
    {
      name: 'Trust Tech Digital',
      role: 'Development & Strategy Partner',
      bio: '20 years of innovation in digital healthcare. Dedicated to solving real problems for practitioners with cutting-edge technology.',
      image: null
    }
  ]

  return (
    <div>
      {/* Hero Section */}
      <section className="bg-gradient-to-b from-primary-600 via-primary-700 to-primary-900 py-20 relative overflow-hidden">
        <div className="absolute top-0 right-0 w-96 h-96 bg-primary-400 rounded-full blur-3xl opacity-20"></div>
        <div className="absolute bottom-0 left-0 w-96 h-96 bg-accent-500 rounded-full blur-3xl opacity-20"></div>

        <div className="container-custom text-center relative z-10">
          <motion.div
            initial={{ opacity: 0, y: 20 }}
            animate={{ opacity: 1, y: 0 }}
            transition={{ duration: 0.6 }}
          >
            <h1 className="text-5xl md:text-6xl font-bold text-white mb-6 leading-tight tracking-tight">
              Our Mission: Empowering Wellness Practitioners
            </h1>
            <p className="text-xl text-primary-100 max-w-3xl mx-auto leading-relaxed">
              We believe that when practitioners spend less time on paperwork,
              they can spend more time changing lives.
            </p>
          </motion.div>
        </div>
      </section>

      {/* Story Section */}
      <section className="section-padding bg-white">
        <div className="container-custom max-w-4xl">
          <motion.div
            initial={{ opacity: 0, y: 20 }}
            whileInView={{ opacity: 1, y: 0 }}
            transition={{ duration: 0.6 }}
            viewport={{ once: true }}
            className="text-center mb-16"
          >
            <h2 className="text-4xl md:text-5xl font-bold text-gray-900 mb-6">
              Our Story
            </h2>
          </motion.div>

          <motion.div
            initial={{ opacity: 0, y: 20 }}
            whileInView={{ opacity: 1, y: 0 }}
            transition={{ duration: 0.6 }}
            viewport={{ once: true }}
            className="prose prose-lg max-w-none"
          >
            <p className="text-lg text-gray-700 leading-relaxed mb-6">
              After 20 years serving the medical and wellness industry, we identified a critical problem:
              practitioners across every discipline—therapists, coaches, nutritionists, chiropractors—were
              struggling with fragmented, outdated software that didn't meet their needs.
            </p>
            <p className="text-lg text-gray-700 leading-relaxed mb-6">
              We saw practitioners wasting hours on administrative tasks instead of focusing on client care.
              Spreadsheets for scheduling, separate billing systems, paper notes, and calendar apps that never
              synced properly. We knew that with our deep understanding of healthcare and technology, we could
              build something transformative.
            </p>
            <p className="text-lg text-gray-700 leading-relaxed mb-6">
              In 2023, we launched Acuity Practice—a platform built by people who understand healthcare,
              for practitioners who deserve better. Today, practitioners everywhere are using Acuity Practice
              to reclaim their time and focus on what matters: their clients' wellbeing.
            </p>
            <p className="text-lg text-gray-700 leading-relaxed">
              Our tagline, "Bringing clarity to your practice," reflects our commitment
              to simplifying practice management so you can focus on what matters most:
              your clients' wellbeing.
            </p>
          </motion.div>
        </div>
      </section>

      {/* Values Section */}
      <section className="section-padding bg-gradient-to-br from-gray-900 via-gray-800 to-gray-900 relative overflow-hidden">
        <div className="absolute top-0 right-0 w-96 h-96 bg-primary-600 rounded-full blur-3xl opacity-10"></div>
        <div className="absolute bottom-0 left-0 w-96 h-96 bg-accent-600 rounded-full blur-3xl opacity-10"></div>

        <div className="container-custom relative z-10">
          <motion.div
            initial={{ opacity: 0, y: 20 }}
            whileInView={{ opacity: 1, y: 0 }}
            transition={{ duration: 0.6 }}
            viewport={{ once: true }}
            className="text-center mb-16"
          >
            <h2 className="text-4xl md:text-5xl font-bold text-white mb-4">
              Our Values
            </h2>
            <p className="text-xl text-gray-300 max-w-2xl mx-auto">
              The principles that guide everything we do
            </p>
          </motion.div>

          <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-4 gap-8">
            {values.map((value, index) => {
              const cardColors = [
                { bg: 'bg-blue-600', text: 'text-blue-400' },
                { bg: 'bg-orange-600', text: 'text-orange-400' },
                { bg: 'bg-teal-600', text: 'text-teal-400' },
                { bg: 'bg-green-600', text: 'text-green-400' }
              ]
              const color = cardColors[index % cardColors.length]
              return (
                <motion.div
                  key={index}
                  initial={{ opacity: 0, y: 20 }}
                  whileInView={{ opacity: 1, y: 0 }}
                  transition={{ duration: 0.5, delay: index * 0.1 }}
                  viewport={{ once: true }}
                  className="text-center"
                >
                  <div className={`inline-flex items-center justify-center w-20 h-20 ${color.bg} text-white rounded-full mb-6 shadow-lg hover:shadow-2xl transition-all duration-300 transform hover:-translate-y-2`}>
                    {value.icon}
                  </div>
                  <h3 className="text-xl font-semibold text-white mb-3">
                    {value.title}
                  </h3>
                  <p className="text-gray-300 leading-relaxed">
                    {value.description}
                  </p>
                </motion.div>
              )
            })}
          </div>
        </div>
      </section>

      {/* Timeline Section */}
      <section className="section-padding bg-white">
        <div className="container-custom max-w-4xl">
          <motion.div
            initial={{ opacity: 0, y: 20 }}
            whileInView={{ opacity: 1, y: 0 }}
            transition={{ duration: 0.6 }}
            viewport={{ once: true }}
            className="text-center mb-16"
          >
            <h2 className="text-4xl md:text-5xl font-bold text-gray-900 mb-4">
              Our Journey
            </h2>
            <p className="text-xl text-gray-600">
              From idea to impact
            </p>
          </motion.div>

          <div className="relative">
            {/* Timeline line */}
            <div className="absolute left-8 top-0 bottom-0 w-0.5 bg-primary-200"></div>

            <div className="space-y-12">
              {timeline.map((item, index) => (
                <motion.div
                  key={index}
                  initial={{ opacity: 0, x: -20 }}
                  whileInView={{ opacity: 1, x: 0 }}
                  transition={{ duration: 0.5, delay: index * 0.1 }}
                  viewport={{ once: true }}
                  className="relative pl-20"
                >
                  <div className="absolute left-0 top-0 w-16 h-16 bg-primary-600 text-white rounded-full flex items-center justify-center font-bold shadow-lg">
                    {item.year}
                  </div>
                  <div className="bg-gray-50 rounded-lg p-6 shadow-md">
                    <h3 className="text-2xl font-semibold text-gray-900 mb-3">
                      {item.title}
                    </h3>
                    <p className="text-gray-600 leading-relaxed">
                      {item.description}
                    </p>
                  </div>
                </motion.div>
              ))}
            </div>
          </div>
        </div>
      </section>

      {/* Team Section */}
      <section className="section-padding bg-primary-600 text-white">
        <div className="container-custom">
          <motion.div
            initial={{ opacity: 0, y: 20 }}
            whileInView={{ opacity: 1, y: 0 }}
            transition={{ duration: 0.6 }}
            viewport={{ once: true }}
            className="text-center mb-16"
          >
            <h2 className="text-4xl md:text-5xl font-bold mb-4">
              Meet the Team
            </h2>
            <p className="text-xl text-primary-100 max-w-2xl mx-auto">
              Passionate people building the future of wellness practice management
            </p>
          </motion.div>

          <div className="grid grid-cols-1 md:grid-cols-2 gap-8 max-w-3xl mx-auto">
            {team.map((member, index) => (
              <motion.div
                key={index}
                initial={{ opacity: 0, y: 20 }}
                whileInView={{ opacity: 1, y: 0 }}
                transition={{ duration: 0.5, delay: index * 0.1 }}
                viewport={{ once: true }}
                className="bg-white bg-opacity-10 backdrop-blur-lg rounded-lg p-8 text-center"
              >
                <div className="w-32 h-32 bg-white bg-opacity-20 rounded-full mx-auto mb-6 flex items-center justify-center">
                  <span className="text-4xl font-bold">{member.name.charAt(0)}</span>
                </div>
                <h3 className="text-2xl font-semibold mb-2">{member.name}</h3>
                <p className="text-primary-100 font-medium mb-4">{member.role}</p>
                <p className="text-primary-50 leading-relaxed">{member.bio}</p>
              </motion.div>
            ))}
          </div>
        </div>
      </section>

      {/* CTA Section */}
      <section className="section-padding bg-gradient-to-br from-primary-900 via-primary-800 to-primary-900 relative overflow-hidden">
        <div className="absolute top-0 right-0 w-96 h-96 bg-primary-600 rounded-full blur-3xl opacity-15"></div>
        <div className="absolute bottom-0 left-0 w-96 h-96 bg-accent-600 rounded-full blur-3xl opacity-15"></div>

        <div className="container-custom text-center relative z-10">
          <motion.div
            initial={{ opacity: 0, y: 20 }}
            whileInView={{ opacity: 1, y: 0 }}
            transition={{ duration: 0.6 }}
            viewport={{ once: true }}
            className="bg-white rounded-2xl p-12 md:p-16 shadow-2xl max-w-3xl mx-auto"
          >
            <h2 className="text-4xl md:text-5xl font-bold text-gray-900 mb-6">
              Join Our Growing Community
            </h2>
            <p className="text-xl text-gray-700 mb-8">
              Be part of a movement to transform wellness practice management.
            </p>
            <a href="https://acuitypractice.com" className="btn-primary text-lg">
              Start Free Trial
            </a>
          </motion.div>
        </div>
      </section>
    </div>
  )
}

export default AboutPage
